/** Container size for blob background. */
const BLOB_CONTAINER_SIZE = 1200;

/** Primary blur effect strength. */
const PRIMARY_BLUR = 'blur(120px)';

/** Secondary blur effect strength. */
const SECONDARY_BLUR = 'blur(60px)';

/** Configuration for animated gradient blobs. */
const BLOB_CONFIGS = [
  {
    width: '120%',
    height: '120%',
    background: 'radial-gradient(circle, var(--blob-color-1) 0%, transparent 70%)',
    top: '-10%',
    left: '-20%',
    animation: 'onboarding-blob1 4s ease-in-out infinite',
  },
  {
    width: '110%',
    height: '110%',
    background: 'radial-gradient(circle, var(--blob-color-2) 0%, transparent 70%)',
    top: '0%',
    right: '-20%',
    animation: 'onboarding-blob2 5s ease-in-out infinite',
  },
  {
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, var(--blob-color-3) 0%, transparent 70%)',
    bottom: '-20%',
    left: '0%',
    animation: 'onboarding-blob3 4.5s ease-in-out infinite',
  },
  {
    width: '90%',
    height: '90%',
    background: 'radial-gradient(circle, var(--blob-color-4) 0%, transparent 70%)',
    top: '-15%',
    right: '-10%',
    animation: 'onboarding-blob4 3.5s ease-in-out infinite',
  },
] as const;

interface BlobBackgroundProps {
  /**
   * `fixed` fills the viewport (onboarding pages); `absolute` fills the
   * nearest positioned ancestor (e.g. the setup page's canvas pane).
   */
  position?: 'fixed' | 'absolute';
  /**
   * Freezes the blob animations in place. The moving blobs force the two
   * stacked blur layers to re-rasterize every frame, so pause them whenever
   * the background is only a faint tint (e.g. the setup canvas after the
   * ready state settles).
   */
  paused?: boolean;
}

/**
 * Animated gradient blob background for onboarding pages.
 *
 * Renders four animated gradient blobs with blur effects to create
 * a dynamic, colorful background. Uses CSS animations defined in index.css.
 *
 * @returns Blob background component.
 */
export const BlobBackground = ({ position = 'fixed', paused = false }: BlobBackgroundProps) => {
  return (
    <div className={`pointer-events-none ${position} inset-0 overflow-hidden`}>
      <div
        className="absolute bottom-[-60%] left-1/2 -translate-x-1/2"
        style={{
          width: BLOB_CONTAINER_SIZE,
          height: BLOB_CONTAINER_SIZE,
          filter: PRIMARY_BLUR,
        }}
      >
        <div className="absolute inset-0" style={{ filter: SECONDARY_BLUR }}>
          {BLOB_CONFIGS.map((config, index) => {
            return (
              <div
                key={index}
                className="absolute rounded-full"
                style={{ ...config, animationPlayState: paused ? 'paused' : undefined }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
